import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './Reports.css';

const Reports = () => {
  const { companyId } = useCompany();
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportParams, setReportParams] = useState({
    asOfDate: new Date().toISOString().split('T')[0],
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Balance General
  const { data: balanceSheet, isLoading: loadingBalance } = useQuery({
    queryKey: ['balanceSheet', companyId, reportParams.asOfDate],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/reports/balance-sheet?asOfDate=${reportParams.asOfDate}`);
      return response.data.data.balanceSheet;
    },
    enabled: !!companyId && selectedReport === 'balance'
  });

  // Estado de Resultados
  const { data: incomeStatement, isLoading: loadingIncome } = useQuery({
    queryKey: ['incomeStatement', companyId, reportParams.startDate, reportParams.endDate],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/reports/income-statement?startDate=${reportParams.startDate}&endDate=${reportParams.endDate}`);
      return response.data.data.incomeStatement;
    },
    enabled: !!companyId && selectedReport === 'income'
  });

  // Balance de Prueba
  const { data: trialBalance, isLoading: loadingTrial } = useQuery({
    queryKey: ['trialBalance', companyId, reportParams.asOfDate],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/reports/trial-balance?asOfDate=${reportParams.asOfDate}`);
      return response.data.data.trialBalance;
    },
    enabled: !!companyId && selectedReport === 'trial'
  });

  const renderBalanceSheet = () => {
    if (loadingBalance) return <div>Cargando Balance General...</div>;
    if (!balanceSheet) return null;

    return (
      <div className="report-content">
        <div className="report-header">
          <h3>Balance General</h3>
          <p>Al {new Date(balanceSheet.asOfDate).toLocaleDateString()}</p>
        </div>

        <div className="balance-sheet">
          <div className="balance-section">
            <h4>ACTIVO</h4>
            <table className="report-table">
              <tbody>
                {balanceSheet.assets.accounts.map((acc, idx) => (
                  <tr key={idx}>
                    <td>{acc.code}</td>
                    <td>{acc.name}</td>
                    <td className="amount">${acc.balance.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan="2"><strong>TOTAL ACTIVO</strong></td>
                  <td className="amount"><strong>${balanceSheet.assets.total.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="balance-section">
            <h4>PASIVO</h4>
            <table className="report-table">
              <tbody>
                {balanceSheet.liabilities.accounts.map((acc, idx) => (
                  <tr key={idx}>
                    <td>{acc.code}</td>
                    <td>{acc.name}</td>
                    <td className="amount">${acc.balance.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan="2"><strong>TOTAL PASIVO</strong></td>
                  <td className="amount"><strong>${balanceSheet.liabilities.total.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="balance-section">
            <h4>PATRIMONIO</h4>
            <table className="report-table">
              <tbody>
                {balanceSheet.equity.accounts.map((acc, idx) => (
                  <tr key={idx}>
                    <td>{acc.code}</td>
                    <td>{acc.name}</td>
                    <td className="amount">${acc.balance.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan="2"><strong>TOTAL PATRIMONIO</strong></td>
                  <td className="amount"><strong>${balanceSheet.equity.total.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="balance-summary">
            <p>
              <strong>Total Pasivo + Patrimonio:</strong> ${balanceSheet.totalLiabilitiesAndEquity.toFixed(2)}
            </p>
            <p className={balanceSheet.isBalanced ? 'balanced' : 'unbalanced'}>
              {balanceSheet.isBalanced ? '✓ Balanceado' : '✗ No balanceado'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderIncomeStatement = () => {
    if (loadingIncome) return <div>Cargando Estado de Resultados...</div>;
    if (!incomeStatement) return null;

    return (
      <div className="report-content">
        <div className="report-header">
          <h3>Estado de Resultados</h3>
          <p>
            Del {new Date(incomeStatement.period.startDate).toLocaleDateString()} al{' '}
            {new Date(incomeStatement.period.endDate).toLocaleDateString()}
          </p>
        </div>

        <div className="income-statement">
          <div className="income-section">
            <h4>INGRESOS</h4>
            <table className="report-table">
              <tbody>
                {incomeStatement.income.accounts.map((acc, idx) => (
                  <tr key={idx}>
                    <td>{acc.code}</td>
                    <td>{acc.name}</td>
                    <td className="amount">${acc.amount.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan="2"><strong>TOTAL INGRESOS</strong></td>
                  <td className="amount"><strong>${incomeStatement.income.total.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="expense-section">
            <h4>GASTOS</h4>
            <table className="report-table">
              <tbody>
                {incomeStatement.expenses.accounts.map((acc, idx) => (
                  <tr key={idx}>
                    <td>{acc.code}</td>
                    <td>{acc.name}</td>
                    <td className="amount">${acc.amount.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan="2"><strong>TOTAL GASTOS</strong></td>
                  <td className="amount"><strong>${incomeStatement.expenses.total.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="net-income">
            <p>
              <strong>UTILIDAD NETA:</strong>{' '}
              <span className={incomeStatement.netIncome >= 0 ? 'positive' : 'negative'}>
                ${incomeStatement.netIncome.toFixed(2)}
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderTrialBalance = () => {
    if (loadingTrial) return <div>Cargando Balance de Prueba...</div>;
    if (!trialBalance) return null;

    return (
      <div className="report-content">
        <div className="report-header">
          <h3>Balance de Prueba</h3>
          <p>Al {new Date(trialBalance.asOfDate).toLocaleDateString()}</p>
        </div>

        <table className="report-table trial-balance-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Cuenta</th>
              <th>Débito</th>
              <th>Crédito</th>
              <th>Saldo</th>
            </tr>
          </thead>
          <tbody>
            {trialBalance.accounts.map((acc, idx) => (
              <tr key={idx}>
                <td>{acc.code}</td>
                <td>{acc.name}</td>
                <td className="amount">${acc.debit.toFixed(2)}</td>
                <td className="amount">${acc.credit.toFixed(2)}</td>
                <td className="amount">${acc.balance.toFixed(2)}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td colSpan="2"><strong>TOTALES</strong></td>
              <td className="amount"><strong>${trialBalance.totals.debit.toFixed(2)}</strong></td>
              <td className="amount"><strong>${trialBalance.totals.credit.toFixed(2)}</strong></td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <div className="balance-summary">
          <p className={trialBalance.isBalanced ? 'balanced' : 'unbalanced'}>
            {trialBalance.isBalanced ? '✓ Balanceado' : '✗ No balanceado'}
          </p>
          {!trialBalance.isBalanced && (
            <p>Diferencia: ${trialBalance.totals.difference.toFixed(2)}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="reports">
      <div className="page-header">
        <h2>Reportes Contables</h2>
      </div>

      <div className="reports-container">
        <div className="reports-sidebar">
          <h3>Reportes Disponibles</h3>
          <button
            className={`report-button ${selectedReport === 'balance' ? 'active' : ''}`}
            onClick={() => setSelectedReport('balance')}
          >
            Balance General
          </button>
          <button
            className={`report-button ${selectedReport === 'income' ? 'active' : ''}`}
            onClick={() => setSelectedReport('income')}
          >
            Estado de Resultados
          </button>
          <button
            className={`report-button ${selectedReport === 'trial' ? 'active' : ''}`}
            onClick={() => setSelectedReport('trial')}
          >
            Balance de Prueba
          </button>
        </div>

        <div className="reports-main">
          {selectedReport && (
            <div className="report-params">
              {selectedReport === 'balance' || selectedReport === 'trial' ? (
                <div className="param-group">
                  <label>Fecha de Corte:</label>
                  <input
                    type="date"
                    value={reportParams.asOfDate}
                    onChange={(e) => setReportParams({ ...reportParams, asOfDate: e.target.value })}
                  />
                </div>
              ) : (
                <>
                  <div className="param-group">
                    <label>Fecha Inicio:</label>
                    <input
                      type="date"
                      value={reportParams.startDate}
                      onChange={(e) => setReportParams({ ...reportParams, startDate: e.target.value })}
                    />
                  </div>
                  <div className="param-group">
                    <label>Fecha Fin:</label>
                    <input
                      type="date"
                      value={reportParams.endDate}
                      onChange={(e) => setReportParams({ ...reportParams, endDate: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {selectedReport === 'balance' && renderBalanceSheet()}
          {selectedReport === 'income' && renderIncomeStatement()}
          {selectedReport === 'trial' && renderTrialBalance()}

          {!selectedReport && (
            <div className="empty-state">
              <p>Selecciona un reporte para visualizarlo</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;

